import { ChangeDetectorRef, inject } from '@angular/core';
import {
  FormGroupDirective,
  NgControl,
  NgForm,
  UntypedFormControl,
} from '@angular/forms';
import { Subject } from 'rxjs';

import { AbstractConstructor, Constructor } from '@aw/shared/models';

import { ErrorStateMatcher } from '../providers/error-state-matcher';
import { CanUpdateErrorState } from './error-state-mixin.model';

type CanUpdateErrorStateCtor = Constructor<CanUpdateErrorState> &
  AbstractConstructor<CanUpdateErrorState>;

export function mixinErrorState<T extends AbstractConstructor<any>>(
  base: T,
): CanUpdateErrorStateCtor & T;

// eslint-disable-next-line no-redeclare
export function mixinErrorState<T extends Constructor<any>>(
  base: T,
): CanUpdateErrorStateCtor & T {
  return class extends base {
    readonly parentForm = inject(NgForm, { optional: true });

    readonly parentFormGroup = inject(FormGroupDirective, { optional: true });

    readonly ngControl = inject(NgControl, { optional: true });

    readonly defaultErrorMatcher = inject(ErrorStateMatcher);

    readonly changeDetector = inject(ChangeDetectorRef);

    readonly stateChanges = new Subject<void>();

    /** Whether the component is in an error state. */
    hasError = false;

    /** An object used to control the error state of the component. */
    errorStateMatcher!: ErrorStateMatcher;

    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(...args: any[]) {
      super(...args);
    }

    /** Updates the error state based on the provided error state matcher. */
    updateErrorState() {
      const matcher = this.errorStateMatcher || this.defaultErrorMatcher;
      const parent = this.parentFormGroup || this.parentForm;

      const control = this.ngControl
        ? (this.ngControl.control as UntypedFormControl)
        : null;

      const oldState = this.hasError;
      const newState = matcher.isErrorState(control, parent);

      if (newState !== oldState) {
        this.hasError = newState;
        this.stateChanges.next();
        this.changeDetector.markForCheck();
      }
    }
  };
}
